import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallbackExperiment } from "@million/lint/runtime";
import { Core } from "@faire/design-tokens";
import { useMaybeAssignCategoryTreeExperimentForFilterSection } from "@faire/retailer-visitor-shared/components/Filter/useMaybeAssignCategoryTreeExperimentForFilterSection";
import { getSearchInputPlaceholder } from "@faire/retailer-visitor-shared/components/FilterPills/consts";
import { FilterOption } from "@faire/retailer-visitor-shared/components/Filters/__internal__/FilterOption";
import { clearFilterSection, DEFAULT_OPTION_GROUP_NAME, getFilteredOptionsGroupMap, getSelectedOptions, isDefaultShowAllFilterSection, SEE_MORE_STEP, SEE_MORE_THRESHOLD } from "@faire/retailer-visitor-shared/components/Filters/FilterUtils";
import { useFilterSectionSelectedChange } from "@faire/retailer-visitor-shared/components/Filters/useFilterSectionSelectedChange";
import { assignSettingOnEvent } from "@faire/retailer-visitor-shared/settings/assignSettingOnEvent";
import { useAssignMillionLintFiltersOptimization, useSettingMillionLintFiltersOptimization } from "@faire/retailer-visitor-shared/settings/useSettingMillionLintFiltersOptimization";
import { useRecordMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Badge as SlateBadge } from "@faire/web--source/slate/Badge";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { SearchInput } from "@faire/web--source/slate/SearchInput";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackRetailersFilterInteraction } from "@faire/web-api--source/events/retailers/actionUnknown/filterInteraction";
import { trackRetailersFilterClearClick } from "@faire/web-api--source/events/retailers/click/filterClear";
import { IFilterSection } from "@faire/web-api--source/indigofair/data/IFilterSection";
import React, { useEffect, useRef } from "react";
import styled from "styled-components";
export var FilterSection = function(param) {
    var name = param.name, section = param.section, _param_initiallyCollapsed = param.initiallyCollapsed, initiallyCollapsed = _param_initiallyCollapsed === void 0 ? true : _param_initiallyCollapsed, _param_collapsable = param.collapsable, collapsable = _param_collapsable === void 0 ? true : _param_collapsable, onChange = param.onChange, className = param.className, showAll = param.showAll, showLoading = param.showLoading, _param_showUnformattedResultsCount = param.showUnformattedResultsCount, showUnformattedResultsCount = _param_showUnformattedResultsCount === void 0 ? false : _param_showUnformattedResultsCount, divRef = param.divRef;
    // Used for functions that need sections reference and are unstable with sections inside the useCallback dependency
    var sectionRef = useRef(section);
    sectionRef.current = section;
    // Listen for .is_selected changes inside the section options to appropriately re-render the options */
    useFilterSectionSelectedChange(function() {
        return getSelectedOptions(section).length;
    });
    var shouldMemoMillionLint = useSettingMillionLintFiltersOptimization();
    var assignMillionLintFiltersOptimization = useAssignMillionLintFiltersOptimization();
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _section_is_invisible;
    var isInvisible = (_section_is_invisible = section.is_invisible) !== null && _section_is_invisible !== void 0 ? _section_is_invisible : false;
    var _React_useState = _sliced_to_array(React.useState(SEE_MORE_THRESHOLD), 2), visibleOptionCount = _React_useState[0], setVisibleOptionCount = _React_useState[1];
    var maybeAssignCategoryTreeExperimentForFilterSection = useMaybeAssignCategoryTreeExperimentForFilterSection();
    useEffect(function() {
        if (showAll) {
            setVisibleOptionCount(SEE_MORE_STEP + SEE_MORE_THRESHOLD);
        }
    }, [
        showAll
    ]);
    useEffect(function() {
        assignMillionLintFiltersOptimization();
    }, [
        assignMillionLintFiltersOptimization
    ]);
    var _React_useState1 = _sliced_to_array(React.useState(""), 2), searchValue = _React_useState1[0], setSearchValue = _React_useState1[1];
    var recordMilestone = useRecordMilestone();
    var isDefaultShowAllSection = isDefaultShowAllFilterSection(section);
    var handleMilestone = useCallbackExperiment(function(milestone) {
        if (!milestone || !milestone.retailer_milestone) {
            return;
        }
        recordMilestone(milestone.retailer_milestone);
    }, [
        recordMilestone
    ], shouldMemoMillionLint);
    var optionsGroupMapResults = React.useMemo(function() {
        return getFilteredOptionsGroupMap(section, searchValue, !isDefaultShowAllSection ? visibleOptionCount : undefined);
    }, [
        searchValue,
        section,
        isDefaultShowAllSection,
        visibleOptionCount
    ]);
    var selectedOptions = getSelectedOptions(section);
    var _React_useState2 = _sliced_to_array(React.useState(initiallyCollapsed), 2), collapsed = _React_useState2[0], setCollapsed = _React_useState2[1];
    var viewRef = useDoActionWhenVisible(function() {
        maybeAssignCategoryTreeExperimentForFilterSection(section, section.options.slice(0, visibleOptionCount).map(function(option) {
            return option.key;
        }));
    });
    var settingAssignmentRef = useDoActionWhenVisible(function() {
        var _section_settings_to_assign;
        (_section_settings_to_assign = section.settings_to_assign) === null || _section_settings_to_assign === void 0 ? void 0 : _section_settings_to_assign.forEach(function(setting) {
            assignSettingOnEvent(setting);
        });
    });
    var handleSubCategoryExpandEvent = useCallbackExperiment(function(filterOption) {
        var section = sectionRef.current;
        maybeAssignCategoryTreeExperimentForFilterSection(section, filterOption.sub_options.map(function(option) {
            return option.key;
        }));
    }, [
        maybeAssignCategoryTreeExperimentForFilterSection
    ], shouldMemoMillionLint);
    var handleHeaderClick = useCallbackExperiment(function() {
        if (!collapsable) {
            return;
        }
        var _section_field_name;
        trackRetailersFilterInteraction(name, collapsed ? "expand_section" : "collapse_section", (_section_field_name = section.field_name) !== null && _section_field_name !== void 0 ? _section_field_name : "");
        setCollapsed(!collapsed);
    }, [
        collapsable,
        collapsed,
        name,
        section.field_name
    ], shouldMemoMillionLint);
    var handleSearchChange = useCallbackExperiment(function(event) {
        setSearchValue(event.target.value);
    }, [], shouldMemoMillionLint);
    var handleSearchClear = useCallbackExperiment(function() {
        setSearchValue("");
    }, [], shouldMemoMillionLint);
    var handleChange = useCallbackExperiment(function(section, filterKey, value) {
        var _section_badge;
        handleMilestone((_section_badge = section.badge) === null || _section_badge === void 0 ? void 0 : _section_badge.dismiss_milestone);
        onChange(section, filterKey, value);
    }, [
        handleMilestone,
        onChange
    ], shouldMemoMillionLint);
    var handleSectionClear = useCallbackExperiment(function(event) {
        var section = sectionRef.current;
        event.stopPropagation();
        var _section_field_name;
        trackRetailersFilterClearClick(name, "section_clear", (_section_field_name = section.field_name) !== null && _section_field_name !== void 0 ? _section_field_name : "");
        clearFilterSection(section);
        handleChange(section, "", false);
        useFilterSectionSelectedChange.getState().optionChanged(shouldMemoMillionLint);
    }, [
        handleChange,
        name
    ], shouldMemoMillionLint);
    var handleShowSeeMore = useCallbackExperiment(function() {
        var section = sectionRef.current;
        setVisibleOptionCount(visibleOptionCount + SEE_MORE_STEP);
        maybeAssignCategoryTreeExperimentForFilterSection(section, section.options.map(function(option) {
            return option.key;
        }));
    }, [
        maybeAssignCategoryTreeExperimentForFilterSection,
        visibleOptionCount
    ], shouldMemoMillionLint);
    var handleShowSeeLess = useCallbackExperiment(function() {
        setVisibleOptionCount(SEE_MORE_THRESHOLD);
    }, [], shouldMemoMillionLint);
    var renderOption = useCallbackExperiment(// eslint-disable-next-line @faire/no-inline-hoc-fc
    function(option, isSubOption) {
        if (!option.key) {
            return null;
        }
        var section = sectionRef.current;
        return /*#__PURE__*/ _jsx(FilterOption, {
            isSubOption: isSubOption,
            section: section,
            option: option,
            searchValue: searchValue,
            onChange: handleChange,
            renderOption: renderOption,
            onExpand: handleSubCategoryExpandEvent,
            showUnformattedResultsCount: showUnformattedResultsCount
        }, "option-".concat(option.key));
    }, [
        handleChange,
        handleSubCategoryExpandEvent,
        searchValue
    ], shouldMemoMillionLint);
    var renderOptionGroup = useCallbackExperiment(// eslint-disable-next-line @faire/no-inline-hoc-fc
    function(groupName, options) {
        return /*#__PURE__*/ _jsxs(React.Fragment, {
            children: [
                groupName !== DEFAULT_OPTION_GROUP_NAME ? /*#__PURE__*/ _jsx(FilterGroupTitle, {
                    variant: "paragraphSansMedium",
                    children: groupName
                }) : null,
                /*#__PURE__*/ _jsx(OptionsWrapper, {
                    children: options.map(function(option) {
                        return renderOption(option, false);
                    })
                })
            ]
        }, "option-groups-".concat(groupName));
    }, [
        renderOption
    ], shouldMemoMillionLint);
    var renderSeeMore = function() {
        if (optionsGroupMapResults.totalFilteredOptions <= SEE_MORE_THRESHOLD || isDefaultShowAllSection) {
            return null;
        }
        return /*#__PURE__*/ _jsx(OptionsWrapper, {
            children: section.options.length > visibleOptionCount ? /*#__PURE__*/ _jsx(Button, {
                variant: "tertiary",
                onClick: handleShowSeeMore,
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "3Uop6y",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Show more"
                        }
                    ]
                })
            }) : /*#__PURE__*/ _jsx(Button, {
                variant: "tertiary",
                onClick: handleShowSeeLess,
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "sCPzsF",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Show less"
                        }
                    ]
                })
            })
        });
    };
    var renderCollapsedSection = function() {
        if (showLoading) {
            return;
        }
        return /*#__PURE__*/ _jsx(SelectedOptionsText, {
            selectedOptions: selectedOptions
        });
    };
    if (!showLoading && !optionsGroupMapResults.optionsGroupMap) {
        return null;
    }
    var _section_display_name;
    return /*#__PURE__*/ _jsx("div", {
        ref: settingAssignmentRef,
        "data-test-id": "filter-section-impression-wrapper-".concat(section.display_name),
        children: /*#__PURE__*/ _jsxs(Wrapper, {
            className: "".concat(className !== null && className !== void 0 ? className : "", " ").concat(isInvisible ? "hidden" : ""),
            ref: divRef,
            "data-test-id": "filter-section-wrapper-".concat(section.display_name),
            children: [
                /*#__PURE__*/ _jsxs(Header, {
                    role: "heading",
                    "aria-level": 3,
                    "aria-label": (_section_display_name = section.display_name) !== null && _section_display_name !== void 0 ? _section_display_name : strictLocalize({
                        id: "7NriMG",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Loading"
                            }
                        ]
                    }),
                    "aria-expanded": !collapsed,
                    "aria-disabled": !collapsable,
                    "aria-controls": "filter-section-options-".concat(section.field_name),
                    id: "filter-section-header-".concat(section.field_name),
                    onClick: handleHeaderClick,
                    $collapsable: collapsable,
                    children: [
                        /*#__PURE__*/ _jsx(Column, {
                            children: showLoading ? /*#__PURE__*/ _jsx(LoadingTextPlaceholder, {}) : /*#__PURE__*/ _jsxs(Row, {
                                children: [
                                    /*#__PURE__*/ _jsx(Badge, {
                                        section: section
                                    }),
                                    /*#__PURE__*/ _jsx(StyledHeaderText, {
                                        variant: "paragraphSansMedium",
                                        children: section.display_name
                                    })
                                ]
                            })
                        }),
                        selectedOptions.length > 0 && section.field_function !== IFilterSection.FieldFunction.SORT ? /*#__PURE__*/ _jsx(ClearAllColumn, {
                            children: /*#__PURE__*/ _jsx(Button, {
                                variant: "tertiary",
                                onClick: handleSectionClear,
                                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                    id: "Z7s5LH",
                                    defaultMessage: [
                                        {
                                            type: 0,
                                            value: "Clear"
                                        }
                                    ]
                                })
                            })
                        }) : null
                    ]
                }),
                /*#__PURE__*/ _jsx("section", {
                    id: "filter-section-options-".concat(section.field_name),
                    "aria-labelledby": "filter-section-header-".concat(section.field_name),
                    hidden: collapsed,
                    ref: viewRef,
                    children: !collapsed ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(FilterSectionScrollable, {
                                role: "list",
                                "aria-label": "".concat(section.display_name),
                                children: [
                                    section.searchable ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SearchInput, {
                                                onChange: handleSearchChange,
                                                onClear: handleSearchClear,
                                                placeholder: getSearchInputPlaceholder(section.search_label_text, section.display_name, strictLocalize),
                                                value: searchValue,
                                                className: "search-filter-field",
                                                "data-test-id": "filter-search-field-".concat(section.field_name)
                                            }),
                                            /*#__PURE__*/ _jsx(Spacer, {
                                                height: "3x"
                                            })
                                        ]
                                    }) : null,
                                    Object.entries(optionsGroupMapResults.optionsGroupMap).map(function(param) {
                                        var _param = _sliced_to_array(param, 2), groupName = _param[0], filterOptions = _param[1];
                                        return renderOptionGroup(groupName, filterOptions);
                                    }),
                                    renderSeeMore(),
                                    searchValue !== "" && optionsGroupMapResults.totalFilteredOptions === 0 ? /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraphSansRegular",
                                        color: Core.text.subdued,
                                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                            id: "kqbRcv",
                                            defaultMessage: [
                                                {
                                                    type: 0,
                                                    value: "No results"
                                                }
                                            ]
                                        })
                                    }) : null
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                height: "3x"
                            })
                        ]
                    }) : null
                }),
                collapsed ? renderCollapsedSection() : null
            ]
        })
    });
};
var Badge = function(param) {
    var section = param.section;
    var strictLocalize = useStrictLocalization().strictLocalize;
    if (!section.badge || section.badge.badge_type !== "NEW_BADGE") {
        return null;
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SlateBadge, {
                backgroundColor: Core.surface.success,
                content: strictLocalize({
                    id: "94ABFq",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "New"
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                width: "1x"
            })
        ]
    });
};
var SelectedOptionsText = function(param) {
    var selectedOptions = param.selectedOptions;
    var maxNumberOfOptionsToShow = 4;
    var numberOfMore = selectedOptions.length - maxNumberOfOptionsToShow;
    if (selectedOptions.length < 1) {
        return null;
    }
    var names = selectedOptions.slice(0, maxNumberOfOptionsToShow).map(function(option) {
        return option.display_name;
    }).join(", ");
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Typography, {
                variant: "paragraphSansRegular",
                color: Core.text.subdued,
                children: [
                    names,
                    numberOfMore > 0 ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            " ",
                            /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "3XuKb9",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "+ "
                                    },
                                    {
                                        type: 1,
                                        value: "moreOptionsNumber"
                                    },
                                    {
                                        type: 0,
                                        value: " more"
                                    }
                                ],
                                values: {
                                    moreOptionsNumber: numberOfMore
                                }
                            })
                        ]
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "3x"
            })
        ]
    });
};
var Wrapper = styled.div.withConfig({
    componentId: "sc-dcb6f74d-0"
})([
    "width:100%;"
]);
var Header = styled(Row).withConfig({
    componentId: "sc-dcb6f74d-1"
})([
    "cursor:",
    ";padding:",
    ";"
], function(props) {
    return props.$collapsable ? "pointer" : "unset";
}, getSpacing("3x", 0));
var ClearAllColumn = styled(Column).withConfig({
    componentId: "sc-dcb6f74d-2"
})([
    "margin-left:auto;margin-right:",
    ";"
], getSpacing("2x"));
var FilterGroupTitle = styled(Typography).withConfig({
    componentId: "sc-dcb6f74d-3"
})([
    "&&&{margin-top:",
    ";margin-bottom:",
    ";",
    "{margin-top:",
    ";}}"
], getSpacing("3x"), getSpacing("2x"), responsiveMediaQueries.mobile, getSpacing("4x"));
var OptionsWrapper = styled(Row).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-dcb6f74d-4"
})([
    "position:relative;width:100%;flex-wrap:wrap;"
]);
var FilterSectionScrollable = styled(Column).withConfig({
    componentId: "sc-dcb6f74d-5"
})([
    "position:relative;width:100%;max-height:100%;overflow-y:auto;padding-right:",
    ";"
], getSpacing("2x"));
var LoadingTextPlaceholder = styled.div.withConfig({
    componentId: "sc-dcb6f74d-6"
})([
    "height:16px;width:129px;background-color:",
    ";"
], Core.surface.subdued);
var StyledHeaderText = styled(Typography).withConfig({
    componentId: "sc-dcb6f74d-7"
})([
    "&&&{-ms-word-break:break-word;word-break:break-word;white-space:pre-wrap;padding-right:",
    ";}"
], getSpacing("1x"));
